import { SearchableSelect } from "@/components/ui/searchable-select";
import React, { useEffect, useRef, useState } from "react";
import { api, unwrapObject, unwrapArray } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { FormSkeleton } from "@/components/ui/page-skeletons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/patterns";
import { Loader2, ImagePlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LetterheadCard } from "@/components/settings/LetterheadCard";
import { TimeFormatChoice, datePreview } from "@/components/settings/TimeFormatChoice";
import { INDIAN_STATES, stateFromGstin } from "@/lib/indianStates";
import type { Country, Currency } from "@/types";

type SettingRule = { id: string; rule_key: string; value_json: any };

/** The defaults new quotes and proposals start from; each is a setting rule */
const DEFAULT_RULES = [
  { key: "QUOTE_PREFIX", label: "Quote number prefix", mono: true, kind: "text" as const },
  { key: "QUOTE_VALIDITY_DAYS", label: "Quote valid for (days)", kind: "number" as const },
  { key: "PROPOSAL_VALIDITY_DAYS", label: "Proposal valid for (days)", kind: "number" as const },
];

/** Every time zone the browser knows, with the company's current one always present */
const zoneList = (current: string) => {
  let all: string[] = [];
  try { all = (Intl as any).supportedValuesOf("timeZone"); } catch { /* older browser */ }
  return all.includes(current) ? all : [current, ...all];
};

const selectClass = "h-9 w-full rounded-md border border-input bg-card px-2.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary";

function Card({ title, sub, children }: { title: string; sub?: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3 rounded-[10px] border border-border bg-card px-4 py-3.5">
      <div>
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </div>
      {children}
    </section>
  );
}

const Field = ({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) => (
  <div className="min-w-0 space-y-1.5">
    <Label className="text-[12.5px] font-medium">{label}</Label>
    {children}
    {hint && <p className="text-[11.5px] text-muted-foreground">{hint}</p>}
  </div>
);

/** Settings → Company profile: business details, bank details, logo and the defaults new quotes start from */
export function CompanyPage() {
  const { refreshUser } = useAuth();
  const { success, error } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoRev, setLogoRev] = useState<string | null>(null); // cache-buster; null = no logo
  const fileRef = useRef<HTMLInputElement>(null);
  const [countries, setCountries] = useState<Country[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [rules, setRules] = useState<SettingRule[]>([]);
  const [defaults, setDefaults] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState("");

  const [form, setForm] = useState({
    id: "",
    name: "",
    address: "",
    country_id: "",
    currency_id: "",
    postal_code: "",
    email: "",
    website: "",
    uen_no: "",
    gst_no: "",
    cin: "",
    state: "",
    bank_name: "",
    bank_acct_no: "",
    telephone_no: "",
    fax_no: "",
    invoice_footer: "",
    time_format: "12h",
    time_zone: "Asia/Kolkata",
    date_format: "dmy",
  });

  const snapshot = (f: typeof form, d: Record<string, string>) => JSON.stringify([f, d]);
  const dirty = !loading && snapshot(form, defaults) !== saved;

  const loadData = async () => {
    setLoading(true);
    try {
      const [compRes, countryRes, currRes, ruleRes] = await Promise.all([
        api.get("/api/Company/Get"),
        api.get("/api/Country/List"),
        api.get("/api/Currency/List"),
        api.get("/api/SettingRule/List"),
      ]);
      const comp = unwrapObject<any>(compRes);
      setLogoRev(comp.logo_url ? String(comp.logo_url) : null);
      const next = {
        id: comp.id || "",
        name: comp.name || comp.CompanyName || "",
        address: comp.address || comp.AddressLine1 || "",
        country_id: String(comp.country_id || comp.CountryId || ""),
        currency_id: String(comp.currency_id || comp.CurrencyId || ""),
        postal_code: comp.postal_code || comp.PostalCode || "",
        email: comp.email || comp.Email || "",
        website: comp.website || comp.Website || "",
        uen_no: comp.uen_no || comp.RegistrationNumber || comp.GstNumber || "",
        gst_no: comp.gst_no || "",
        cin: comp.cin || "",
        state: comp.state || "",
        bank_name: comp.bank_name || comp.BankName || "",
        bank_acct_no: comp.bank_acct_no || comp.BankAccountNumber || "",
        telephone_no: comp.telephone_no || comp.Phone || "",
        fax_no: comp.fax_no || "",
        invoice_footer: comp.invoice_footer || "",
        time_format: comp.time_format === "24h" ? "24h" : "12h",
        time_zone: comp.time_zone || "Asia/Kolkata",
        date_format: comp.date_format === "mdy" ? "mdy" : "dmy",
      };
      const all = unwrapArray<SettingRule>(ruleRes);
      const d: Record<string, string> = {};
      for (const r of DEFAULT_RULES) d[r.key] = String(all.find((x) => x.rule_key === r.key)?.value_json ?? "");
      setForm(next);
      setRules(all);
      setDefaults(d);
      setSaved(snapshot(next, d));
      setCountries(unwrapArray<Country>(countryRes));
      setCurrencies(unwrapArray<Currency>(currRes));
    } catch (err: any) {
      error("Could not load", err.message || "Failed to load company details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const save = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!form.name || !form.email || !form.country_id || !form.currency_id) {
      error("Validation Error", "Please fill the company name, email, country and currency");
      return;
    }
    for (const r of DEFAULT_RULES) {
      if (r.kind === "number" && !(Number(defaults[r.key]) >= 1)) return error("Validation Error", `${r.label} must be 1 or more`);
      if (r.kind === "text" && !defaults[r.key]?.trim()) return error("Validation Error", `${r.label} can't be empty`);
    }
    setSaving(true);
    try {
      await api.patch("/api/Company/Update", form);
      const prev = JSON.parse(saved)[1] as Record<string, string>;
      for (const r of DEFAULT_RULES) {
        if (defaults[r.key] === prev[r.key]) continue;
        const rule = rules.find((x) => x.rule_key === r.key);
        if (!rule) continue;
        await api.put(`/api/SettingRule/Update/${rule.id}`, { rule_key: rule.rule_key, rule_name: r.label, value_json: r.kind === "number" ? Number(defaults[r.key]) : defaults[r.key]!.trim() });
      }
      success("Company profile saved", "New quotes and proposals use these details");
      await loadData();
      await refreshUser(); // the signed-in person's clock may follow the company default
    } catch (err: any) {
      error("Save failed", err.message || "Failed to update company settings");
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (file?: File | null) => {
    if (!file) return;
    if (!/^image\/(png|jpeg|webp)$/.test(file.type)) return error("Invalid file", "Use a PNG, JPG or WebP image");
    if (file.size > 1024 * 1024) return error("File too large", "The logo must be 1 MB or smaller");
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.upload("/api/Company/Logo", fd);
      const res = unwrapObject<any>(await api.get("/api/Company/Get"));
      setLogoRev(res.logo_url ? String(res.logo_url) : null);
      success("Logo updated", "It will appear on new proposals and quotations");
    } catch (err: any) {
      error("Upload failed", err.message || "Could not upload the logo");
    } finally {
      setLogoBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    setLogoBusy(true);
    try {
      await api.delete("/api/Company/Logo");
      setLogoRev(null);
      success("Logo removed", "Documents will show the company name instead");
    } catch (err: any) {
      error("Remove failed", err.message || "Could not remove the logo");
    } finally {
      setLogoBusy(false);
    }
  };

  if (loading) return <FormSkeleton />;

  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  return (
    <form onSubmit={save} className="space-y-4">
      <PageHeader
        title="Company profile"
        actions={
          <>
            {dirty && <span className="text-xs font-medium text-warning">Unsaved changes</span>}
            <Button type="button" variant="outline" size="sm" disabled={!dirty || saving} onClick={loadData}>
              Discard
            </Button>
            <Button type="button" size="sm" disabled={!dirty || saving} onClick={() => save()}>
              {saving ? <Loader2 className="size-4 animate-spin" /> : "Save changes"}
            </Button>
          </>
        }
      />
      <p className="text-[13px] text-muted-foreground">Your business details, logo and the defaults new quotes start from.</p>

      <div className="flex flex-wrap items-start gap-3">
        <div className="flex min-w-0 flex-[999_1_560px] flex-col gap-3">
          <Card title="Business details" sub="Printed on quotations, proposals and invoices.">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Company name">
                <Input required value={form.name} onChange={(e) => set({ name: e.target.value })} />
              </Field>
              <Field label="Registration number">
                <Input value={form.uen_no} onChange={(e) => set({ uen_no: e.target.value })} />
              </Field>
              <Field label="GSTIN">
                <Input
                  value={form.gst_no}
                  maxLength={15}
                  onChange={(e) => {
                    const gst_no = e.target.value.toUpperCase();
                    set({ gst_no, state: form.state || stateFromGstin(gst_no) });
                  }}
                  className="font-mono uppercase"
                />
              </Field>
              <Field label="CIN" hint="21 characters. Printed on the letterhead footer and quotations.">
                <Input value={form.cin} maxLength={21} onChange={(e) => set({ cin: e.target.value.toUpperCase().replace(/\s/g, "") })} className="font-mono uppercase" placeholder="U72900TN2020PTC000000" />
              </Field>
              <Field label="GST registration state" hint="Customers in this state get CGST + SGST; other states get IGST.">
                <SearchableSelect value={form.state} onChange={(e) => set({ state: e.target.value })} className={selectClass}>
                  <option value="">Select state</option>
                  {INDIAN_STATES.map((st) => (
                    <option key={st.code} value={st.name}>
                      {st.name}
                    </option>
                  ))}
                </SearchableSelect>
              </Field>
            </div>
            <Field label="Registered address">
              <Textarea required rows={2} value={form.address} onChange={(e) => set({ address: e.target.value })} />
            </Field>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Field label="Postal code">
                <Input required value={form.postal_code} onChange={(e) => set({ postal_code: e.target.value })} />
              </Field>
              <Field label="Country">
                <SearchableSelect required value={form.country_id} onChange={(e) => set({ country_id: e.target.value })} className={selectClass}>
                  <option value="">Select country</option>
                  {countries.map((c: any) => (
                    <option key={c.id || c.Id} value={String(c.id || c.Id)}>
                      {c.name || c.CountryName}
                    </option>
                  ))}
                </SearchableSelect>
              </Field>
              <Field label="Phone">
                <Input value={form.telephone_no} onChange={(e) => set({ telephone_no: e.target.value })} />
              </Field>
              <Field label="Sales email">
                <Input required type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Website">
                  <Input value={form.website} onChange={(e) => set({ website: e.target.value })} />
                </Field>
              </div>
            </div>
          </Card>

          <Card title="Bank details" sub="Shown under Payment on quotations.">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Bank and branch">
                <Input value={form.bank_name} onChange={(e) => set({ bank_name: e.target.value })} />
              </Field>
              <Field label="Account number">
                <Input value={form.bank_acct_no} onChange={(e) => set({ bank_acct_no: e.target.value })} className="font-mono" />
              </Field>
            </div>
            <Field label="Footer on invoices and quotations">
              <Textarea rows={2} value={form.invoice_footer} onChange={(e) => set({ invoice_footer: e.target.value })} placeholder="Thank you for your business. Terms: net 30 days." />
            </Field>
          </Card>
        </div>

        <div className="flex min-w-0 flex-[1_1_300px] flex-col gap-3">
          <Card title="Logo">
            <div className="flex items-center gap-3.5">
              <span className="flex h-[84px] w-[110px] shrink-0 items-center justify-center rounded-xl border border-border bg-white p-2">
                {logoRev ? <img src={`/api/Company/Logo?v=${encodeURIComponent(logoRev)}`} alt="Company logo" className="max-h-full max-w-full object-contain" /> : <span className="text-center text-[11px] text-muted-foreground">No logo yet</span>}
              </span>
              <span className="flex flex-col items-start gap-1.5">
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} />
                <Button type="button" variant="outline" size="sm" disabled={logoBusy} onClick={() => fileRef.current?.click()}>
                  {logoBusy ? <Loader2 className="size-4 animate-spin" /> : <ImagePlus className="size-4" />}
                  {logoRev ? "Upload new logo" : "Upload logo"}
                </Button>
                {logoRev && (
                  <button type="button" disabled={logoBusy} onClick={removeLogo} className="text-xs font-medium text-danger hover:underline">
                    Remove
                  </button>
                )}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">PNG, JPG or WebP, up to 1 MB. A transparent PNG works best.</p>
          </Card>

          <LetterheadCard />

          <Card title="Where this appears">
            {["Quotation PDF", "Proposal and PDF", "Customer proposal page"].map((a) => (
              <div key={a} className="flex items-center justify-between border-t border-border/60 py-1.5 text-[13px]">
                <span>{a}</span>
                <span className={`rounded-full px-2 py-0.5 text-[11.5px] font-semibold ${logoRev ? "bg-success-soft text-success" : "bg-muted text-muted-foreground"}`}>{logoRev ? "Shows logo" : "Shows name"}</span>
              </div>
            ))}
          </Card>

          <Card title="Date and time" sub="How times and dates are written for everyone who hasn't chosen their own (in Settings → Display), and in emails and PDFs.">
            <TimeFormatChoice
              name="company-time-format"
              value={form.time_format}
              onChange={(v) => set({ time_format: v })}
              options={[
                { value: "12h", label: "12-hour", format: "12h" },
                { value: "24h", label: "24-hour", format: "24h" },
              ]}
            />
            <TimeFormatChoice
              name="company-date-format"
              value={form.date_format}
              onChange={(v) => set({ date_format: v })}
              options={[
                { value: "dmy", label: "Day first", format: null, preview: datePreview("dmy") },
                { value: "mdy", label: "Month first", format: null, preview: datePreview("mdy") },
              ]}
            />
            <Field label="Time zone" hint="Where the business runs. The morning digest goes out at its morning, and times in emails and PDFs for customers use it. Each person's own messages use the zone of their browser.">
              <SearchableSelect value={form.time_zone} onChange={(e) => set({ time_zone: e.target.value })} className={selectClass}>
                {zoneList(form.time_zone).map((z) => <option key={z} value={z}>{z.replace(/_/g, " ")}</option>)}
              </SearchableSelect>
            </Field>
          </Card>

          <Card title="Defaults" sub="What a new quote or proposal starts with.">
            <Field label="Base currency">
              <SearchableSelect required value={form.currency_id} onChange={(e) => set({ currency_id: e.target.value })} className={selectClass}>
                <option value="">Select currency</option>
                {currencies.map((c: any) => (
                  <option key={c.id || c.Id} value={String(c.id || c.Id)}>
                    {c.name || c.CurrencyName} ({c.code || c.CurrencyCode})
                  </option>
                ))}
              </SearchableSelect>
            </Field>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {DEFAULT_RULES.map((r) => (
                <Field key={r.key} label={r.label}>
                  <Input
                    type={r.kind === "number" ? "number" : "text"}
                    min={r.kind === "number" ? 1 : undefined}
                    value={defaults[r.key] ?? ""}
                    disabled={!rules.some((x) => x.rule_key === r.key)}
                    onChange={(e) => setDefaults((d) => ({ ...d, [r.key]: e.target.value }))}
                    className={r.mono ? "font-mono" : ""}
                  />
                </Field>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </form>
  );
}
